import React from "react"
import { cn } from "@/lib/utils"

/**
 * Skeleton Component
 * 
 * Replaces content with same shape placeholder that indicates a loading state.
 * Based on Radix UI Skeleton API.
 * 
 * @example
 * ```tsx
 * <Skeleton>Loading</Skeleton>
 * <Skeleton width="48px" height="48px" />
 * <Skeleton loading={false}>Actual content</Skeleton>
 * ```
 */

export interface SkeletonProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Whether to show skeleton or children */
  loading?: boolean
  /** Fixed width */
  width?: string
  /** Fixed height */
  height?: string
  /** Minimum width */
  minWidth?: string
  /** Maximum width */
  maxWidth?: string
  /** Minimum height */
  minHeight?: string
  /** Maximum height */
  maxHeight?: string
  /** Children to display when not loading */
  children?: React.ReactNode
}

export const Skeleton = React.forwardRef<HTMLSpanElement, SkeletonProps>(
  (
    {
      loading = true,
      width,
      height,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      children,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const skeletonStyle: React.CSSProperties = {
      ...style,
      width,
      height,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
    }

    if (!loading && children) {
      // When not loading, render children normally
      return <>{children}</>
    }

    if (loading && children) {
      // When loading with children, preserve dimensions and disable interactions
      return (
        <span
          ref={ref}
          className={cn(
            "skeleton-wrapper",
            "inline-block relative",
            className
          )}
          style={skeletonStyle}
          aria-busy="true"
          aria-live="polite"
          {...props}
        >
          {/* Hidden children to preserve dimensions */}
          <span
            className="invisible"
            aria-hidden="true"
            style={{ pointerEvents: "none" }}
          >
            {children}
          </span>
          
          {/* Skeleton overlay */}
          <span
            className={cn(
              "skeleton",
              "absolute inset-0",
              "rounded-[var(--radius-2)]",
              "bg-[var(--gray-a3)]",
              "animate-skeleton-pulse"
            )}
            aria-hidden="true"
          />
        </span>
      )
    }

    // When loading without children, render just the skeleton
    return (
      <span
        ref={ref}
        className={cn(
          "skeleton",
          "inline-block",
          "rounded-[var(--radius-2)]",
          "bg-[var(--gray-a3)]",
          "animate-skeleton-pulse",
          // Default dimensions if not provided
          !width && !minWidth && !maxWidth && "w-full",
          !height && !minHeight && !maxHeight && "h-4",
          className
        )}
        style={skeletonStyle}
        aria-busy="true"
        aria-live="polite"
        {...props}
      />
    )
  }
)

Skeleton.displayName = "Skeleton"

/**
 * SkeletonText - Helper component for text skeletons
 * Automatically sizes to match text content
 */
export interface SkeletonTextProps extends Omit<SkeletonProps, 'children'> {
  /** Number of lines */
  lines?: number
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({ 
  lines = 1, 
  className,
  ...props 
}) => {
  if (lines === 1) {
    return <Skeleton className={cn("h-4", className)} {...props} />
  }

  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          className={cn("h-4", i === lines - 1 && "w-3/4", className)} 
          {...props}
        />
      ))}
    </div>
  )
}

SkeletonText.displayName = "SkeletonText"

